import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Send } from "lucide-react";
import { adminApi } from "@/lib/api";
import { selectCls } from "../managed/shared";

const ACTIVE = ["running", "paused"];

export const usePublish = (target) => {
  const [camps, setCamps] = useState([]);
  const [cid, setCid] = useState(target?.cid || "");
  const [day, setDay] = useState(target?.day || null);
  const [info, setInfo] = useState({ lead_ids: [] });
  const [busy, setBusy] = useState("");
  useEffect(() => {
    adminApi.managedList().then((d) => {
      const act = d.campaigns.filter((c) => ACTIVE.includes(c.status));
      setCamps(act);
      if (act.length === 1 && !target?.cid) setCid(act[0].id);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!cid) { setInfo({ lead_ids: [] }); return; }
    adminApi.publishedLeads(cid).then((d) => { setInfo(d); setDay((x) => (x && x <= d.current_day ? x : d.current_day)); })
      .catch(() => setInfo({ lead_ids: [] }));
  }, [cid]);
  const toggle = async (lead) => {
    const shown = info.lead_ids.includes(lead.id);
    setBusy(lead.id);
    try {
      if (shown) {
        await adminApi.unpublishLead(cid, lead.id);
        setInfo((p) => ({ ...p, lead_ids: p.lead_ids.filter((x) => x !== lead.id) }));
        toast.success(`${lead.company_name} nascosto al cliente`);
      } else {
        const r = await adminApi.publishLead(cid, lead.id, day);
        const n = r.added.contacts + r.added.ig + r.added.tiktok;
        if (!n && !r.skipped) throw new Error("Nessun dato pubblicabile per questo lead");
        setInfo((p) => ({ ...p, lead_ids: [...p.lead_ids, lead.id] }));
        toast.success(`${lead.company_name} visibile al cliente · giorno ${r.day}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(""); }
  };
  return { camps, cid, setCid, day, setDay, info, busy, toggle };
};

export const PublishTarget = ({ p }) => (
  <div data-testid="publish-target" className="flex flex-wrap items-center gap-3 border border-[#E0262C]/40 bg-[#fdecec] px-4 py-3">
    <Send className="w-4 h-4 text-[#E0262C]" />
    <span className="text-sm text-[#2B3036]">Mostra lead al cliente nella campagna</span>
    <select data-testid="publish-campaign-select" value={p.cid} onChange={(e) => p.setCid(e.target.value)} className={`${selectCls} md:w-[420px]`}>
      <option value="">{p.camps.length ? "Seleziona campagna in corso…" : "Nessuna campagna in corso"}</option>
      {p.camps.map((c) => <option key={c.id} value={c.id}>{`${c.client_name || c.client_email} · ${c.params?.area || ""} · ${(c.params?.skill || "").slice(0, 40)}`}</option>)}
    </select>
    {p.cid && p.info.current_day && (
      <select data-testid="publish-day-select" value={p.day || ""} onChange={(e) => p.setDay(Number(e.target.value))} className={selectCls}>
        {Array.from({ length: p.info.current_day }, (_, i) => i + 1).map((d) => <option key={d} value={d}>Giorno {d}</option>)}
      </select>
    )}
    {p.cid && <span data-testid="publish-info" className="text-[11px] font-mono text-[#5b6168]">{p.info.lead_ids.length} già visibili al cliente</span>}
  </div>
);

export const PublishButton = ({ p, lead }) => {
  const shown = p.info.lead_ids.includes(lead.id);
  if (!p.cid) return <span className="text-[10px] font-mono text-[#6b7280]">—</span>;
  return (
    <button data-testid={`publish-lead-${lead.id}`} onClick={() => p.toggle(lead)} disabled={p.busy === lead.id}
      title={shown ? "Clicca per nascondere al cliente" : "Mostra al cliente"}
      className={`inline-flex items-center gap-1 px-2 h-7 text-[11px] border transition-colors whitespace-nowrap ${shown ? "border-[#10b981] text-[#10b981] bg-[#10b981]/5 hover:bg-[#fdecec] hover:border-[#E0262C] hover:text-[#E0262C]" : "border-[#2B3036] text-[#2B3036] hover:bg-[#2B3036] hover:text-white"}`}>
      {p.busy === lead.id ? <Loader2 className="w-3 h-3 animate-spin" /> : shown ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
      {shown ? "Visibile" : "Mostra"}
    </button>
  );
};
